import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, ArrowLeft, Save, Loader2, Image } from 'lucide-react';
import type { Room } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';

export function RoomsList() {
  const { data, isLoading, error } = useList<Room>({ resource: 'rooms' });
  const { mutate: deleteOne } = useDelete();
  const { show, edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this room?')) {
      deleteOne({ resource: 'rooms', id });
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading rooms: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Rooms</h1>
        <button
          onClick={() => create('rooms')}
          className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Room
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preview
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Size
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Occupancy
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((room) => (
              <tr key={room.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  {room.images?.[0] ? (
                    <img
                      src={room.images[0]}
                      alt={room.name}
                      className="w-16 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <Image className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{room.name}</div>
                  <div className="text-sm text-gray-500">{room.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {room.size} m²
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {room.maxOccupancy} guests
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                  {room.price?.weekday ? `IDR ${room.price.weekday.toLocaleString()}` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => show('rooms', room.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => edit('rooms', room.id)}
                      className="p-2 text-gray-400 hover:text-gold-600 transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(room.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data?.data.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            No rooms found. Click "Add Room" to create one.
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// Create Room
export function RoomsCreate() {
  const navigate = useNavigate();
  const { mutate: createRoom, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    size: 0,
    view: '',
    bedType: '',
    maxOccupancy: 2,
    hasPrivatePool: false,
    features: [] as string[],
    amenities: [] as string[],
    price: { weekday: 0, weekend: 0 },
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nameId) {
      alert('Please fill in the name');
      return;
    }

    const slug = form.slug || generateSlug(form.nameId);

    createRoom(
      {
        resource: 'rooms',
        values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn },
      },
      {
        onSuccess: (data) => {
          const newId = data?.data?.id;
          if (newId) {
            navigate(`/rooms/edit/${newId}`);
          } else {
            navigate('/rooms');
          }
        },
      }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/rooms')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Room</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new room type</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {/* Basic Info */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

          <BilingualField
            label="Name"
            valueEn={form.nameEn}
            valueId={form.nameId}
            onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Room Details */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Room Details</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Size (m²)</label>
              <input
                type="number"
                value={form.size}
                onChange={(e) => setForm({ ...form, size: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Occupancy</label>
              <input
                type="number"
                value={form.maxOccupancy}
                onChange={(e) => setForm({ ...form, maxOccupancy: parseInt(e.target.value) || 2 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
              <input
                type="text"
                value={form.view}
                onChange={(e) => setForm({ ...form, view: e.target.value })}
                placeholder="Ocean, Garden, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bed Type</label>
              <input
                type="text"
                value={form.bedType}
                onChange={(e) => setForm({ ...form, bedType: e.target.value })}
                placeholder="King, Twin, etc."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hasPrivatePool"
              checked={form.hasPrivatePool}
              onChange={(e) => setForm({ ...form, hasPrivatePool: e.target.checked })}
              className="w-4 h-4 text-gold-600 rounded focus:ring-gold-500"
            />
            <label htmlFor="hasPrivatePool" className="text-sm text-gray-700">Has Semi Private Pool</label>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Pricing (IDR)</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekday Price</label>
              <input
                type="number"
                value={form.price.weekday}
                onChange={(e) => setForm({ ...form, price: { ...form.price, weekday: parseInt(e.target.value) || 0 } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekend Price</label>
              <input
                type="number"
                value={form.price.weekend}
                onChange={(e) => setForm({ ...form, price: { ...form.price, weekend: parseInt(e.target.value) || 0 } })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Note about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can add images after creating the room.
          </p>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/rooms')}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
          >
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Room
export function RoomsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<Room>({ resource: 'rooms', id: id! });
  const { mutate: updateRoom, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    size: 0,
    view: '',
    bedType: '',
    maxOccupancy: 2,
    hasPrivatePool: false,
    features: [] as string[],
    amenities: [] as string[],
    price: { weekday: 0, weekend: 0 },
    sortOrder: 0,
  });
  const [images, setImages] = useState<ManagedImage[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const [newAmenity, setNewAmenity] = useState('');

  useEffect(() => {
    if (data?.data) {
      const room = data.data;
      setForm({
        slug: room.slug || '',
        nameEn: (room as any).nameEn || (room as any).name_en || room.name || '',
        nameId: (room as any).nameId || (room as any).name_id || room.name || '',
        descriptionEn: (room as any).descriptionEn || (room as any).description_en || room.description || '',
        descriptionId: (room as any).descriptionId || (room as any).description_id || room.description || '',
        shortDescriptionEn: (room as any).shortDescriptionEn || (room as any).short_description_en || room.shortDescription || '',
        shortDescriptionId: (room as any).shortDescriptionId || (room as any).short_description_id || room.shortDescription || '',
        size: room.size || 0,
        view: room.view || '',
        bedType: room.bedType || '',
        maxOccupancy: room.maxOccupancy || 2,
        hasPrivatePool: room.hasPrivatePool || false,
        features: room.features || [],
        amenities: room.amenities || [],
        price: { weekday: room.price?.weekday || 0, weekend: room.price?.weekend || 0 },
        sortOrder: (room as any).sortOrder ?? 0,
      });
      // Convert image URLs to ManagedImage format
      if (room.images) {
        setImages(
          room.images.map((url, index) => ({
            url,
            imageType: 'gallery' as const,
            sortOrder: index,
          }))
        );
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateRoom(
      {
        resource: 'rooms',
        id: id!,
        values: {
          ...form,
          images: images.map((img) => img.url),
        },
      },
      {
        onSuccess: () => {
          navigate('/rooms');
        },
      }
    );
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setForm({ ...form, features: [...form.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== index) });
  };

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setForm({ ...form, amenities: [...form.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };

  const removeAmenity = (index: number) => {
    setForm({ ...form, amenities: form.amenities.filter((_, i) => i !== index) });
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/rooms')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Room</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Room details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form (2 columns) */}
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

            <BilingualField
              label="Name"
              valueEn={form.nameEn}
              valueId={form.nameId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>

            <BilingualField
              label="Short Description"
              valueEn={form.shortDescriptionEn}
              valueId={form.shortDescriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })}
              multiline
              rows={2}
            />

            <BilingualField
              label="Full Description"
              valueEn={form.descriptionEn}
              valueId={form.descriptionId}
              onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })}
              multiline
              rows={4}
            />
          </div>

          {/* Room Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Room Details</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Size (m²)</label>
                <input
                  type="number"
                  value={form.size}
                  onChange={(e) => setForm({ ...form, size: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Occupancy</label>
                <input
                  type="number"
                  value={form.maxOccupancy}
                  onChange={(e) => setForm({ ...form, maxOccupancy: parseInt(e.target.value) || 2 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
                <input
                  type="text"
                  value={form.view}
                  onChange={(e) => setForm({ ...form, view: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bed Type</label>
                <input
                  type="text"
                  value={form.bedType}
                  onChange={(e) => setForm({ ...form, bedType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="hasPrivatePool"
                checked={form.hasPrivatePool}
                onChange={(e) => setForm({ ...form, hasPrivatePool: e.target.checked })}
                className="w-4 h-4 text-gold-600 rounded focus:ring-gold-500"
              />
              <label htmlFor="hasPrivatePool" className="text-sm text-gray-700">Has Semi Private Pool</label>
            </div>
          </div>

          {/* Features & Amenities */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Features & Amenities</h2>

            <div className="grid grid-cols-2 gap-6">
              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    placeholder="Add feature..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.features.map((feature, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-gold-100 text-gold-800 rounded text-sm"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-gold-600 hover:text-gold-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newAmenity}
                    onChange={(e) => setNewAmenity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())}
                    placeholder="Add amenity..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    onClick={addAmenity}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.amenities.map((amenity, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                    >
                      {amenity}
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Pricing (IDR)</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekday Price</label>
                <input
                  type="number"
                  value={form.price.weekday}
                  onChange={(e) => setForm({ ...form, price: { ...form.price, weekday: parseInt(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weekend Price</label>
                <input
                  type="number"
                  value={form.price.weekend}
                  onChange={(e) => setForm({ ...form, price: { ...form.price, weekend: parseInt(e.target.value) || 0 } })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/rooms')}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50"
            >
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Changes
            </button>
          </div>
        </form>

        {/* Right: Images (1 column) */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Room Images</h2>
            <ImageManager
              entityType="room"
              entityId={id!}
              images={images}
              imageType="gallery"
              multiple={true}
              maxImages={10}
              onChange={setImages}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Show Room
export function RoomsShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { edit } = useNavigation();
  const { data, isLoading } = useOne<Room>({ resource: 'rooms', id: id! });

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  const room = data?.data;
  if (!room) {
    return <div className="p-4">Room not found</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/rooms')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{room.name}</h1>
            <p className="text-sm text-gray-500 mt-1">{room.slug}</p>
          </div>
        </div>
        <button
          onClick={() => edit('rooms', room.id)}
          className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors"
        >
          <Edit className="w-5 h-5" />
          Edit
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Images */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Images</h2>
            {room.images && room.images.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {room.images.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt={`${room.name} - ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-8">No images</div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Size</dt>
                <dd className="font-medium">{room.size} m²</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Max Occupancy</dt>
                <dd className="font-medium">{room.maxOccupancy} guests</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">View</dt>
                <dd className="font-medium">{room.view || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Bed Type</dt>
                <dd className="font-medium">{room.bedType || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Semi Private Pool</dt>
                <dd className="font-medium">{room.hasPrivatePool ? 'Yes' : 'No'}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Weekday</dt>
                <dd className="font-medium">
                  {room.price?.weekday ? `IDR ${room.price.weekday.toLocaleString()}` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Weekend</dt>
                <dd className="font-medium">
                  {room.price?.weekend ? `IDR ${room.price.weekend.toLocaleString()}` : '-'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
